// src/lib/status/client.ts
import { env } from '$env/dynamic/public';
import type { Heartbeat, Incident, MonitorStatus, StatusLevel, StatusSnapshot } from './types';

const BASE = env.PUBLIC_UPTIME_KUMA_URL ?? 'https://status.admrl.co';
const SLUG = env.PUBLIC_UPTIME_KUMA_STATUS_SLUG ?? 'api';

const HEARTBEAT_CODE: Record<number, StatusLevel> = {
    0: 'down',
    1: 'operational',
    2: 'degraded',
    3: 'maintenance'
};

function mapHeartbeat(hb: any): Heartbeat {
    return {
        status: HEARTBEAT_CODE[hb?.status] ?? 'unknown',
        time: new Date(hb?.time ?? Date.now()).toISOString(),
        msg: typeof hb?.msg === 'string' ? hb.msg : '',
        ping: typeof hb?.ping === 'number' ? hb.ping : null
    };
}

function deriveLevel(heartbeats: Heartbeat[]): StatusLevel {
    return heartbeats.at(-1)?.status ?? 'unknown';
}

function uptimeFromHeartbeats(heartbeats: Heartbeat[], hours: number): number | null {
    const since = Date.now() - hours * 3_600_000;
    const inWindow = heartbeats.filter((h) => new Date(h.time).getTime() >= since);
    if (!inWindow.length) return null;
    const up = inWindow.filter((h) => h.status === 'operational' || h.status === 'maintenance').length;
    return up / inWindow.length;
}

async function fetchBadgeUptime(monitorId: number, hours: number): Promise<number | null> {
    try {
        const res = await fetch(`${BASE}/api/badge/${monitorId}/uptime/${hours}`, {
            headers: { accept: 'image/svg+xml' }
        });
        if (!res.ok) return null;
        const svg = await res.text();
        const matches = [...svg.matchAll(/([\d.]+)\s*%/g)];
        if (!matches.length) return null;
        const pct = parseFloat(matches[matches.length - 1][1]);
        return Number.isFinite(pct) ? pct / 100 : null;
    } catch {
        return null;
    }
}

const SEVERITY: StatusLevel[] = ['operational', 'maintenance', 'degraded', 'down', 'unknown'];
function worst(a: StatusLevel, b: StatusLevel): StatusLevel {
    return SEVERITY.indexOf(b) > SEVERITY.indexOf(a) ? b : a;
}

function emptySnapshot(ok: boolean): StatusSnapshot {
    return {
        monitors: [
            { id: -1, name: 'Admiral API', level: 'unknown', uptime24h: null, uptime30d: null, avgPing: null, heartbeats: [] }
        ],
        incident: null,
        overall: 'unknown',
        fetchedAt: new Date().toISOString(),
        ok
    };
}

export async function getStatusSnapshot(): Promise<StatusSnapshot> {
    try {
        const [configRes, beatRes] = await Promise.all([
            fetch(`${BASE}/api/status-page/${SLUG}`, { headers: { accept: 'application/json' } }),
            fetch(`${BASE}/api/status-page/heartbeat/${SLUG}`, { headers: { accept: 'application/json' } })
        ]);

        if (!configRes.ok || !beatRes.ok) return emptySnapshot(false);

        const config = await configRes.json();
        const beat = await beatRes.json();

        const groups: any[] = config?.publicGroupList ?? [];
        const monitorMeta = groups.flatMap((g) => g?.monitorList ?? []);
        const heartbeatList: Record<string, any[]> = beat?.heartbeatList ?? {};
        const uptimeList: Record<string, number> = beat?.uptimeList ?? {};

        const monitors: MonitorStatus[] = await Promise.all(
            monitorMeta.map(async (m: any) => {
                const id = m.id;
                const heartbeats = (heartbeatList[id] ?? []).map(mapHeartbeat);
                const pings = heartbeats.map((h) => h.ping).filter((p): p is number => p != null);

                const uptime24h =
                    uptimeList[`${id}_24`] ?? (await fetchBadgeUptime(id, 24)) ?? uptimeFromHeartbeats(heartbeats, 24);
                const uptime30d =
                    uptimeList[`${id}_720`] ?? (await fetchBadgeUptime(id, 720)) ?? uptimeFromHeartbeats(heartbeats, 720);

                return {
                    id,
                    name: m.name ?? 'Admiral API',
                    level: deriveLevel(heartbeats),
                    uptime24h,
                    uptime30d,
                    avgPing: pings.length ? Math.round(pings.reduce((a, b) => a + b, 0) / pings.length) : null,
                    heartbeats
                };
            })
        );

        const inc = config?.incident;
        const incident: Incident | null = inc
            ? {
                    id: inc.id,
                    title: inc.title ?? 'Service notice',
                    content: inc.content ?? '',
                    style: inc.style ?? 'warning',
                    createdDate: inc.createdDate ? new Date(inc.createdDate).toISOString() : null,
                    lastUpdated: inc.lastUpdated ? new Date(inc.lastUpdated).toISOString() : null
                }
            : null;

        let overall = monitors.reduce<StatusLevel>((acc, m) => worst(acc, m.level), 'operational');
        if (incident?.style === 'danger') overall = worst(overall, 'down');
        else if (incident?.style === 'warning') overall = worst(overall, 'degraded');

        return {
            monitors: monitors.length ? monitors : emptySnapshot(true).monitors,
            incident,
            overall,
            fetchedAt: new Date().toISOString(),
            ok: true
        };
    } catch {
        return emptySnapshot(false);
    }
}
